// Pure helpers for "did the user actually watch this video?" detection.
//
// The core idea is to record every whole second the user has watched into a
// Set, so seeking forward (or skipping straight to the end) doesn't fake
// completion. A video is considered "watched" once the size of that Set
// reaches a configurable fraction of the total duration in seconds.

// Default "live" completion ratio for long videos (over 5 minutes). Lowered
// 0.9 → 0.75 because real users on mobile WebView routinely lose 10–15% of
// timeupdate samples (buffering, fullscreen transitions, throttled iframes),
// then 0.75 → 0.6 (owner decision, 2026-10-06) to match the medium-clip
// ratio. Seek-to-end is still rejected because it accumulates 0 unique
// seconds.
export const WATCH_COMPLETION_RATIO = 0.6;

// Lower threshold accepted only when the player itself fires a real `ended`
// event OR when a loop boundary is detected. Guards against timeupdate gaps
// while still rejecting pure seek-to-end skipping.
export const WATCH_ENDED_RATIO = 0.1;

// Short clips (YouTube Shorts, Vimeo teasers, etc.) — iframe API only ticks
// every ~250ms and Android WebView throttles aggressively, so a 15s short
// can easily lose 5+ unique seconds. 50% is strong enough to require real
// viewing (and reject seek-to-end) without punishing honest watches.
export const SHORT_CLIP_DURATION_S = 30;
export const SHORT_CLIP_COMPLETION_RATIO = 0.5;

// Medium clips (≤5min). Currently the same ratio as long videos
// (WATCH_COMPLETION_RATIO); the tier is kept so the two can be tuned apart.
export const MEDIUM_CLIP_DURATION_S = 300;
export const MEDIUM_CLIP_COMPLETION_RATIO = 0.6;

// When a loop is detected (currentTime jumps backward after the user crossed
// this fraction of the duration), we treat it as a "full pass watched" event
// and downgrade to WATCH_ENDED_RATIO.
export const LOOP_BOUNDARY_RATIO = 0.5;

/**
 * Adds the integer floor of `currentSeconds` to the watched-seconds set.
 * No-ops on invalid input so callers don't need to guard before calling.
 */
export function recordWatchedSecond(
  watched: Set<number>,
  currentSeconds: number,
): void {
  if (!Number.isFinite(currentSeconds) || currentSeconds < 0) return;
  watched.add(Math.floor(currentSeconds));
}

/**
 * Returns the appropriate "live" completion ratio for a given duration.
 * Tiered: short (≤30s) → 50%, medium (≤5min) → 60%, long → 60%.
 */
export function getRatioForDuration(durationSeconds: number): number {
  if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return WATCH_COMPLETION_RATIO;
  }
  if (durationSeconds <= SHORT_CLIP_DURATION_S) {
    return SHORT_CLIP_COMPLETION_RATIO;
  }
  if (durationSeconds <= MEDIUM_CLIP_DURATION_S) {
    return MEDIUM_CLIP_COMPLETION_RATIO;
  }
  return WATCH_COMPLETION_RATIO;
}

/**
 * Returns true when the watched-seconds set covers at least the given ratio
 * of the video's whole-second duration.
 *
 * Returns false for invalid durations (< 1s or non-finite) so completion
 * can't fire before metadata loads.
 */
export function isWatchComplete(
  watched: Set<number>,
  durationSeconds: number,
  ratio: number = WATCH_COMPLETION_RATIO,
): boolean {
  if (!Number.isFinite(durationSeconds) || durationSeconds < 1) return false;
  return watched.size / Math.floor(durationSeconds) >= ratio;
}

export interface WatchTracker {
  record: (currentSeconds: number, durationSeconds: number) => void;
  recordWithLoopDetection: (currentSeconds: number, durationSeconds: number) => void;
  checkEnded: (durationSeconds: number) => void;
  reset: () => void;
  hasFired: () => boolean;
}

/**
 * The completion state machine behind `useWatchTracker`, free of React so it
 * can be tested directly. Three rules can fire `onComplete` (once):
 *
 *   1. `record` / `recordWithLoopDetection`: unique watched seconds reach the
 *      duration tier's ratio (`getRatioForDuration`, or the pinned `getRatio`).
 *   2. `checkEnded`: the player reported `ended` and at least
 *      WATCH_ENDED_RATIO of unique seconds were watched.
 *   3. `recordWithLoopDetection`: playback wrapped around — the reported time
 *      jumped BACKWARD from a position at or past LOOP_BOUNDARY_RATIO — which
 *      is treated like `ended` (auto-looping clips never report an end).
 */
export function createWatchTracker(
  onComplete: () => void,
  getRatio?: () => number | undefined,
): WatchTracker {
  let watched = new Set<number>();
  let completeFired = false;
  let lastTime = 0;

  const fireWithRatio = (durationSeconds: number, effectiveRatio: number) => {
    if (completeFired) return;
    if (!isWatchComplete(watched, durationSeconds, effectiveRatio)) return;
    completeFired = true;
    console.log("[watchTracker] firing onComplete", {
      watched: watched.size,
      durationSeconds,
      effectiveRatio,
    });
    onComplete();
  };

  const fireIfReady = (durationSeconds: number) => {
    // If the caller pinned an explicit ratio, honor it. Otherwise pick a
    // duration-aware default (short clips get a relaxed threshold).
    const pinned = getRatio?.();
    const effective =
      typeof pinned === "number" ? pinned : getRatioForDuration(durationSeconds);
    fireWithRatio(durationSeconds, effective);
  };

  const checkEnded = (durationSeconds: number) => {
    // Real `ended` event (or detected loop boundary): relax to
    // WATCH_ENDED_RATIO (10%) to absorb timeupdate gaps. Seek-to-end is
    // still rejected because it accumulates 0 unique watched seconds.
    fireWithRatio(durationSeconds, WATCH_ENDED_RATIO);
  };

  const record = (currentSeconds: number, durationSeconds: number) => {
    if (completeFired) return;
    recordWatchedSecond(watched, currentSeconds);
    lastTime = currentSeconds;
    fireIfReady(durationSeconds);
  };

  const recordWithLoopDetection = (
    currentSeconds: number,
    durationSeconds: number,
  ) => {
    if (completeFired) return;
    const last = lastTime;
    // Loop detected: time jumped backward and the previous position was
    // past the loop-boundary threshold. Treat as a full pass watched.
    if (
      Number.isFinite(durationSeconds) &&
      durationSeconds >= 1 &&
      last > 0 &&
      currentSeconds + 0.5 < last &&
      last / durationSeconds >= LOOP_BOUNDARY_RATIO
    ) {
      recordWatchedSecond(watched, currentSeconds);
      lastTime = currentSeconds;
      checkEnded(durationSeconds);
      return;
    }
    recordWatchedSecond(watched, currentSeconds);
    lastTime = currentSeconds;
    fireIfReady(durationSeconds);
  };

  const reset = () => {
    watched = new Set();
    completeFired = false;
    lastTime = 0;
  };

  return {
    record,
    recordWithLoopDetection,
    checkEnded,
    reset,
    hasFired: () => completeFired,
  };
}

// Largest forward jump between two player ticks that is still treated as
// continuous playback. Measured 2026-10-26 (Chrome, Android WebView): Livid
// relays every native `timeupdate`, ~0.27s apart at any speed, and its menu
// tops out at 2×, so a member moves ~0.53s per tick (≤0.6s under 6× CPU
// throttle; never over 2s at 2× even at 20×). 2s therefore still fills a 2×
// tick that arrives a full second late. Every seek control (the ±10s
// double-tap, the scrubber) jumps further, so a seek is never gap-filled; an
// honest watch that does lose seconds to bigger jumps still completes on
// `ended` (WATCH_ENDED_RATIO).
export const TICK_GAP_FILL_MAX_S = 2;

/**
 * One player tick for players whose ticks are sparse (Livid via player.js).
 * `prevSeconds` is the previous tick's time (0 before the first tick).
 *
 * When the forward gap is a tick gap (≤ TICK_GAP_FILL_MAX_S) every whole
 * second passed through is credited, so an honest watch on a throttled
 * WebView still reaches the completion ratio. A larger jump is a seek:
 * nothing in between is credited.
 */
export function recordTickWithGapFill(
  tracker: Pick<WatchTracker, "record" | "recordWithLoopDetection">,
  prevSeconds: number,
  seconds: number,
  durationSeconds: number,
): void {
  if (seconds > prevSeconds && seconds - prevSeconds <= TICK_GAP_FILL_MAX_S) {
    // Plain `record`, never the loop-aware recorder: the fill replays seconds
    // at or before the previous tick (it starts at floor(prevSeconds)), which
    // loop detection would read as a backward jump and complete the lesson
    // on the first second boundary past the midpoint.
    for (let s = Math.floor(prevSeconds); s < Math.floor(seconds); s++) {
      tracker.record(s, durationSeconds);
    }
  }
  // Only the real tick is loop-aware: a clip that wraps around reports a
  // time behind the previous tick, and we still credit the user.
  tracker.recordWithLoopDetection(seconds, durationSeconds);
}
